import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useUpdateAccountsLimits } from '@utils/hooks';

import {
  accountsActions,
  notificationsActions,
  transactionsActions,
  useStore,
  walletActions,
} from '@modules';
import { AccountsOperationsEnum } from '@modules/accounts/state';
import { AccountsSellRateResponse, PaymentAliasEnum } from '@modules/accounts/types';

import Button from '@components/Button';
import Converter, { TConverter } from '@containers/Converter';
import Limits from '@containers/Converter/Limits';
import useConverterState from '@containers/Converter/useConverterState';
import { useCustomContext } from '@containers/OperationBlock/context';

import Details from '../../../Details';

import style from './AccountsSellExchange.scss';

const cn = bemCssModules(style, 'AccountsSellExchange');
const ti = getTestId('AccountsSellExchange');

const AccountsSellExchange: CustomFC = ({ className }) => {
  const { t } = useTranslation();

  const [{ accounts, global, wallet }, dispatch] = useStore();
  const { currencies } = global;

  const converterState = useConverterState({
    toCurrencies: [CONST.accountsCurrency],
    fromCurrencies: currencies.crypto,
  });
  const { fromCurrency, fromAmount, toCurrency, toAmount, rate, updateRates } = converterState;
  const sell_token = (rate as AccountsSellRateResponse)?.sell_token;

  const { setSelectedCurrency } = useCustomContext();

  useUpdateAccountsLimits({
    from: fromCurrency,
    to: toCurrency,
    type: AccountsOperationsEnum.fiat_sell,
    payment_method: PaymentAliasEnum.iban_exchange,
  });

  useEffect(() => {
    if (fromCurrency) {
      setSelectedCurrency(fromCurrency);
    }
  }, [fromCurrency]);

  const onSubmit = async e => {
    e.preventDefault();
    if (rate) {
      dispatch(accountsActions.accountSell({ sell_token }))
        .unwrap()
        .then(() => {
          const totalWithCurrency = `${rate?.total[fromCurrency]} ${fromCurrency}`;
          const message = t('notifications.order_processed', { totalWithCurrency });
          dispatch(notificationsActions.add({ message }));
          dispatch(accountsActions.getAccounts());
          dispatch(walletActions.getWallet());
          dispatch(transactionsActions.getTransactions());
        })
        .finally(() => {
          updateRates();
        });
    }
  };

  const disabled = !+fromAmount || !+toAmount || !sell_token;
  const isLoading = accounts.loaders.accountSell;

  const getRate: TConverter['getRate'] = (from, to, amount) => {
    const data = {
      from,
      to,
      amount,
      payment_alias: PaymentAliasEnum.iban_exchange,
    };
    return dispatch(accountsActions.getAccountSellRate(data)).unwrap();
  };
  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <Converter getRate={getRate} {...converterState} />
      <Limits limits={wallet.limits.sell} {...converterState} />
      <Details from={fromCurrency} to={toCurrency} operationRate={rate} />
      <Button
        className={cn('button')}
        type="submit"
        onClick={onSubmit}
        isLoading={isLoading}
        disabled={disabled}
      >
        {t('operations.sell_crypto')}
      </Button>
    </div>
  );
};

export default AccountsSellExchange;
